import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { AppRoutes } from '@/constants';
import { selectChatMatterInfo, useAppSelector } from '@/store';
import { useGetMatterAboutQuery } from '@/api';

const formatTitle = (...parts: string[]) => parts.join(' | ');

// Chat, client, project, file and aerial names can contain PII, so titles carry only case references and page names.
const useRouteTitle = () => {
  const location = useLocation();
  const { pathname } = location;
  const runDate = (location.state as { runDate?: string } | undefined)?.runDate;

  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  const secondSegment = segments[1];
  const thirdSegment = segments[2];

  const chatMatterReference = useAppSelector(selectChatMatterInfo)?.matterReference;

  const caseId = firstSegment === AppRoutes.Cases || firstSegment === AppRoutes.CaseHistory ? secondSegment : undefined;
  const { caseReference } = useGetMatterAboutQuery(
    { matterId: caseId ?? '' },
    {
      skip: !caseId,
      selectFromResult: ({ currentData }) => ({
        caseReference: currentData?.info.caseReference,
      }),
    },
  );

  useEffect(() => {
    if (!pathname) return;

    if (pathname === '/login' || pathname === `/${AppRoutes.Login}` || pathname.includes(AppRoutes.LoginRedirect)) {
      document.title = 'Sign in';
      return;
    }

    if (firstSegment === AppRoutes.Chat) {
      document.title = chatMatterReference ? formatTitle(chatMatterReference, 'Chat') : 'Chat';
      return;
    }

    if (firstSegment === AppRoutes.Cases) {
      if (secondSegment) {
        document.title = caseReference ? formatTitle(caseReference, 'Case') : 'Case';
        return;
      }
      document.title = 'Cases';
      return;
    }

    if (firstSegment === AppRoutes.Projects) {
      if (!secondSegment) {
        document.title = 'Projects';
        return;
      }
      if (thirdSegment === AppRoutes.ProjectChatHistory) {
        document.title = formatTitle('Chat history', 'Project');
        return;
      }
      if (thirdSegment === AppRoutes.Chat) {
        document.title = formatTitle('Chat', 'Project');
        return;
      }
      document.title = 'Project';
      return;
    }

    if (firstSegment === AppRoutes.History) {
      document.title = 'Chat history';
      return;
    }

    if (firstSegment === AppRoutes.CaseHistory) {
      document.title = caseReference ? formatTitle(caseReference, 'Chat history') : 'Chat history';
      return;
    }

    if (firstSegment === AppRoutes.UserManagement) {
      document.title = 'Users';
      return;
    }

    if (firstSegment === AppRoutes.AdminTools) {
      document.title = 'Admin Dashboard';
      return;
    }

    if (firstSegment === AppRoutes.Actions) {
      document.title = 'Actions';
      return;
    }

    if (firstSegment === AppRoutes.Aerials) {
      if (!secondSegment) {
        document.title = 'Aerials';
        return;
      }
      if (thirdSegment === 'runs') {
        document.title = runDate ? formatTitle(runDate, 'Aerial run') : 'Aerial run';
        return;
      }
      document.title = 'Aerial';
      return;
    }

    if (firstSegment === AppRoutes.DocumentViewer) {
      document.title = 'Document';
      return;
    }

    if (firstSegment === AppRoutes.HowWeFletch) {
      document.title = "How we're using Fletch";
      return;
    }

    document.title = 'Chat';
  }, [pathname, firstSegment, secondSegment, thirdSegment, chatMatterReference, caseReference, runDate]);
};

export { useRouteTitle };
